import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { JobSearchContext } from '../../context';
import AutocompleteBootstrap5 from '../../../components/react/autocompletes/autocomplete_bootstrap_5';
import TypeaheadSelectBootstrap5 from '../../../components/react/autocompletes/typeahead_select_bootstrap_5';
import Tooltip from '../../../components/react/tooltip/tooltip';
import Checkbox from '../../../components/react/checkbox/checkbox';
import {
  autocompletePlacesPath,
  autocompleteJobsEmployerCompaniesPath,
} from '../../../utilities/api_relative_paths';
import {
  PUBLICATION_INTERVAL_OPTIONS,
  NEWS_TOPIC_OPTIONS,
} from '../../constants/job_search.constants';
import { STAGE_OPTIONS } from '../../../utilities/constants/job_related.constants';
import { Option } from '../../../components/react/autocompletes/types';
import { useClearMobileFilters, useGetMobileFiltersCount } from '../../hooks';
import styles from './mobile_filters.module.scss';
import typeaheadStyles from '../../../components/react/autocompletes/generic_typeahead.module.scss';
import { useScrollToResults } from '../../hooks';

const MobileFilters = () => {
  const {
    userInfo: { isSignedIn },
    isMobileFiltersOpen,
    setIsMobileFiltersOpen,
    setPageNumber,
    locations,
    setLocations,
    careerLevels,
    jobLevels,
    setJobLevels,
    contractLengths,
    setPositionTypes,
    positionTypes,
    newsTopicAggregations = [],
    setNewsTopics,
    newsTopics,
    orgNames,
    setOrgNames,
    publicationInterval,
    setPublicationInterval,
    stages,
    setStages,
    isRemote,
    setIsRemote,
    hasSalaryData,
    setHasSalaryData,
    type,
    setType,
    isLoading,
    total,
  } = useContext(JobSearchContext);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const scrollToResults = useScrollToResults();

  const clearFilters = useClearMobileFilters();
  const mobileFiltersCount = useGetMobileFiltersCount();

  useEffect(() => {
    if (isMobileFiltersOpen) {
      setFiltersChanged(true);
    }
  }, [
    isRemote,
    locations,
    jobLevels,
    publicationInterval,
    newsTopics,
    positionTypes,
    orgNames,
    stages,
    type,
    hasSalaryData,
  ]);

  useEffect(() => {
    const bodyElement = document.querySelector('body');

    if (isMobileFiltersOpen) return bodyElement.classList.add(styles.scrollDisabled);

    bodyElement.classList.remove(styles.scrollDisabled);
    if (filtersChanged) {
      scrollToResults();
      setFiltersChanged(false);
    }
  }, [isMobileFiltersOpen]);

  if (!isMobileFiltersOpen) return null;

  const handleCloseFilters = () => {
    const footerElement = document.querySelector('footer');
    footerElement.classList.remove('d-none');

    setIsMobileFiltersOpen(false);
  };

  const locationFilter = (
    <div className='px-3'>
      <label className='form-label' htmlFor='jobSearchLocations'>
        Location
      </label>
      <AutocompleteBootstrap5
        id='jobSearchLocations'
        inputProps={{
          'aria-label': 'jobSearchLocations',
          'data-testid': 'jobs-search-location-filter',
          className: 'form-control',
        }}
        apiPath={autocompletePlacesPath}
        minLength={2}
        multiple={true}
        placeholder='e.g. Worldwide'
        selected={locations || []}
        filterBy={(option) => {
          if (!locations?.length) return true;
          return locations.every((element) => element !== option.text);
        }}
        onSelect={(options) => {
          setPageNumber(1);
          setLocations(options.map((entry) => entry?.text || entry));
        }}
      />
    </div>
  );

  const experienceLevelFilter = (
    <div className='px-3'>
      <label className='form-label' htmlFor='jobSearchJobLevel'>
        Experience level
      </label>
      <TypeaheadSelectBootstrap5
        id='jobSearchJobLevel'
        inputProps={{
          'aria-label': 'jobSearchJobLevel',
          className: 'form-control',
        }}
        multiple={true}
        placeholder='All levels'
        onChange={(options: string[]) => {
          if (isLoading) return;

          setPageNumber(1);
          setJobLevels(options);
        }}
        options={careerLevels}
        selected={careerLevels.filter((entry) => jobLevels?.includes(entry))}
      />
    </div>
  );

  const contractTypeFilter = (
    <div className='px-3'>
      <label className='form-label' htmlFor='jobSearchPositionType'>
        Contract type
      </label>
      <TypeaheadSelectBootstrap5
        id='jobSearchPositionType'
        inputProps={{
          'aria-label': 'jobSearchPositionType',
          className: 'form-control',
        }}
        multiple={true}
        placeholder='All positions'
        onChange={(options: string[]) => {
          if (isLoading) return;

          setPageNumber(1);
          setPositionTypes(options);
        }}
        options={contractLengths}
        selected={contractLengths.filter((entry) => positionTypes?.includes(entry))}
      />
    </div>
  );

  const sectorFilter = (
    <div className='px-3'>
      <label className='form-label' htmlFor='jobSearchNewsSector'>
        Sector
      </label>
      <TypeaheadSelectBootstrap5
        id='jobSearchNewsSector'
        inputProps={{
          'aria-label': 'jobSearchNewsSector',
          className: 'form-control',
        }}
        multiple={true}
        placeholder='All sectors'
        onChange={(selectedOptions) => {
          if (isLoading) return;

          setPageNumber(1);
          setNewsTopics(selectedOptions.map((entry) => entry.value || entry));
        }}
        renderMenuItemChildren={(option) => {
          const number =
            newsTopicAggregations?.find((topic) => topic.key === option.value)?.value || 0;
          return (
            <>
              {option.text} ({number})
            </>
          );
        }}
        options={NEWS_TOPIC_OPTIONS}
        selected={NEWS_TOPIC_OPTIONS.filter((entry) => newsTopics?.includes(entry.value))}
      />
    </div>
  );

  const organizationFilter = (
    <div className='px-3'>
      <label className='form-label' htmlFor='jobSearchOrganization'>
        Organization
      </label>
      <AutocompleteBootstrap5
        id='jobSearchOrganization'
        inputProps={{
          'aria-label': 'jobSearchOrganization',
          className: 'form-control',
        }}
        placeholder='e.g. Devex'
        minLength={2}
        multiple={true}
        apiPath={autocompleteJobsEmployerCompaniesPath}
        selected={orgNames || []}
        filterBy={(option) => {
          if (!orgNames?.length) return true;
          return orgNames.every((element) => element !== option.text);
        }}
        onSelect={(options) => {
          setPageNumber(1);
          setOrgNames(options.map((entry) => entry?.text || entry));
        }}
      />
    </div>
  );

  const postedDateFilter = (
    <div>
      <label className='form-label' htmlFor='jobSearchPostedDate'>
        Posted date
      </label>
      <TypeaheadSelectBootstrap5
        id='jobSearchPostedDate'
        inputProps={{
          'aria-label': 'jobSearchPostedDate',
          className: classNames('form-control rounded-1', {
            [typeaheadStyles.activeField]: publicationInterval,
          }),
        }}
        disabled={isLoading}
        placeholder='All time'
        onChange={(selectedOptions: Option[]) => {
          setPageNumber(1);
          setPublicationInterval(selectedOptions[0]?.value);
        }}
        options={PUBLICATION_INTERVAL_OPTIONS}
        selected={PUBLICATION_INTERVAL_OPTIONS.filter(
          (entry) => entry.value === publicationInterval
        )}
        filterBy={(option: { text: string }, data) => {
          if (publicationInterval) return true;
          return option.text.toLowerCase().includes(data.text.toLowerCase());
        }}
      />
    </div>
  );

  const stageFilter = (
    <div id='jobSearchStageInput'>
      <label className='form-label' htmlFor='jobSearchStage'>
        Project stage
      </label>
      <TypeaheadSelectBootstrap5
        id='jobSearchStage'
        inputProps={{
          'aria-label': 'jobSearchStage',
          className: 'form-control',
        }}
        multiple={true}
        renderMenuItemChildren={(option) => {
          return (
            <Tooltip placement='top' message={option.tooltip}>
              <span>
                <i className='text-gray-light fal icon-info-circle small me-2' />
                <span>{option.value}</span>
              </span>
            </Tooltip>
          );
        }}
        placeholder='All stages'
        labelKey='value'
        onChange={(selectedOptions) => {
          if (isLoading) return;

          setPageNumber(1);
          setStages(selectedOptions.map((entry) => entry.value || entry));
        }}
        options={STAGE_OPTIONS}
        selected={STAGE_OPTIONS.filter((entry) => stages?.includes(entry.value))}
      />
    </div>
  );

  const remoteFilter = (
    <div className='d-flex flex-row'>
      <div className='align-self-center'>
        <Checkbox
          ariaLabel='remote only toggle'
          dataTestId='remote-jobs-toggle'
          isSlider={true}
          isToggled={JSON.parse(isRemote)}
          handleChange={() => {
            setPageNumber(1);
            setIsRemote(String(!JSON.parse(isRemote)));
          }}
          disabled={isLoading}
        />
      </div>
      <span className='ms-2 mb-1 align-self-center'>Remote</span>
    </div>
  );

  const forLoggedInBadge = (
    <div
      // eslint-disable-next-line max-len
      className={`${styles.forLoggedInBadge} d-flex justify-content-center align-items-center rounded`}
    >
      <span className='fw-bold text-black'>For logged in users</span>
    </div>
  );

  const withSalaryFilter = (
    <div className='d-flex flex-row position-relative'>
      <div className='align-self-center'>
        <Checkbox
          ariaLabel='with salary data only toggle'
          dataTestId='with-salary-jobs-toggle'
          isSlider={true}
          isToggled={JSON.parse(hasSalaryData)}
          handleChange={() => {
            setPageNumber(1);
            setHasSalaryData(String(!JSON.parse(hasSalaryData)));
          }}
          d-flex
          flex-column
          flex-xl-row
          disabled={!isSignedIn || isLoading}
        />
      </div>
      <span className='ms-2 mb-1 align-self-center'>With salary</span>
      {!isSignedIn && forLoggedInBadge}
    </div>
  );

  const exclusiveJobsFilter = (
    <div className='d-flex flex-row px-3'>
      <div className='align-self-center'>
        <Checkbox
          ariaLabel='exclusive jobs only toggle'
          dataTestId='exclusive-jobs-toggle'
          isSlider={true}
          isToggled={type === 'exclusive jobs'}
          handleChange={() => {
            setPageNumber(1);
            if (type === 'exclusive jobs') return setType('');
            setType('exclusive jobs');
          }}
          disabled={isLoading}
        />
      </div>
      <span className='ms-2 mb-1 align-self-center'>Exclusive jobs</span>
    </div>
  );

  return (
    <div className={styles.container} data-testid='mobile-filters'>
      <div className={styles.headerContainer}>
        <div className={styles.titleContainer}>
          <i
            className='fal icon-arrow-left fs-6'
            onClick={handleCloseFilters}
            aria-label='back to results button'
            data-testid='arrow-back'
          />
          <span className='fs-45 fw-bold' data-testid='filters-header-text'>
            Filters
            {mobileFiltersCount ? ` (${mobileFiltersCount})` : ''}
          </span>
        </div>
        <button
          className='btn btn-outline-secondary'
          onClick={clearFilters}
          disabled={mobileFiltersCount === 0}
          data-testid='reset-filters'
        >
          Reset filters
        </button>
      </div>
      <div className={styles.filtersContainer}>
        {locationFilter}
        {experienceLevelFilter}
        {contractTypeFilter}
        {sectorFilter}
        {organizationFilter}
        <div className={styles.twoColumnContainer}>
          {postedDateFilter}
          {stageFilter}
        </div>
        <div className={styles.twoColumnContainer}>
          {remoteFilter}
          {withSalaryFilter}
        </div>
        {exclusiveJobsFilter}
      </div>
      <div className={styles.seeResultsContainer}>
        <button
          className='btn btn-primary'
          onClick={handleCloseFilters}
          data-testid='see-results-button'
        >
          See results
          {` (${total})`}
        </button>
      </div>
    </div>
  );
};

export default MobileFilters;
